<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import * as d3 from 'd3'
import * as topojson from 'topojson-client'
import world from '@/assets/map/countries-110m.json'
import Countries from '@/assets/map/all.json'

// Geo/Country widget for a single IP. Renders a dark card with a d3 world map
// plus a flag + "City, Country" notch. Coordinates come from the webscout_geo
// enrichment (location.latitude / location.longitude). Three cases:
//   1. trusted coordinates → Mercator centred on the point + a marker
//   2. implausible or country-inconsistent coords but a known country → frame
//      that country instead (geo data is sometimes garbage, e.g. lat -87 for a
//      US Cloudflare IP, or lat 6.77/lon 0 for a German IP → Gulf of Guinea)
//   3. nothing usable      → the empty placeholder
// The IP itself is intentionally not shown (the single-view header carries it).

const props = defineProps({
  location: {
    type: Object,
    default: null
  }
})

const mapEl = ref(null)

// Logical canvas; the SVG scales to the container via viewBox.
const W = 640
const H = 300
// Mercator scale — wide regional zoom, plenty of surrounding context (tunable).
const SCALE = 320

// Concrete token values (d3-created SVG nodes don't inherit scoped CSS vars).
const LAND_FILL = '#2c3644'      // clearly lighter than the water/card so land reads
const LAND_STROKE = 'rgba(255, 255, 255, 0.16)'
const GRATICULE = 'rgba(255, 255, 255, 0.05)'
const HIGHLIGHT_FILL = 'rgba(115, 87, 210, 0.32)'
const ACCENT = '#7357d2'
const ACCENT_TEXT = '#a78bfa'

// alpha-2 ISO → numeric topojson feature id (e.g. "US" → 840).
const alpha2ToId = {}
for (const c of Countries) {
  if (c['alpha-2']) alpha2ToId[c['alpha-2']] = Number(c['country-code'])
}

// Coordinates we can meaningfully centre on. Excludes non-finite, the
// null-island sentinel, and near-pole garbage (Mercator is unusable there and
// no real city sits beyond ~82°).
const coordUsable = computed(() => {
  const l = props.location
  if (!l) return false
  const lat = l.latitude
  const lon = l.longitude
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false
  if (lat === 0 && lon === 0) return false
  return Math.abs(lat) <= 82
})

const flagSrc = computed(() => {
  const iso = props.location?.country_iso
  if (!iso) return null
  try {
    return require('@/assets/flags/4x3/' + iso.toLowerCase() + '.svg')
  } catch (e) {
    return null
  }
})

const caption = computed(() => {
  const l = props.location || {}
  const parts = []
  if (l.city) parts.push(l.city)
  if (l.country_name) parts.push(l.country_name)
  else if (l.country_iso) parts.push(l.country_iso)
  return parts.join(', ')
})

const secondary = computed(() => {
  const l = props.location || {}
  return [l.region, l.continent].filter(Boolean).join(' · ')
})

// Boundary features, built once and cached across instances.
let features = null
function getFeatures() {
  if (!features) {
    features = topojson.feature(world, world.objects.countries).features
  }
  return features
}

// The country polygon to fall back to when coords aren't usable.
const countryFeature = computed(() => {
  const iso = props.location?.country_iso
  if (!iso) return null
  const id = alpha2ToId[iso.toUpperCase()]
  if (id == null) return null
  return getFeatures().find(f => Number(f.id) === id) || null
})

// Degrees of slack around a country's bounding box when cross-checking a
// coordinate against it (tolerates the simplified 110m polygons plus legit
// coastal / near-border points).
const COORD_COUNTRY_PAD = 3

// Whether we actually trust the coordinate enough to drop a marker on it. Geo
// enrichment sometimes emits a plausible-looking coordinate that lands nowhere
// near the reported country (e.g. a German IP tagged lat 6.77 / lon 0 → the
// Gulf of Guinea). When we know the country and the point falls well outside
// its bounding box, we distrust the coordinate and let draw() frame + fill the
// country instead. With no country_iso to cross-check, we fall back to the
// looser coordUsable test.
const coordTrusted = computed(() => {
  if (!coordUsable.value) return false
  const cf = countryFeature.value
  if (!cf) return true
  const lon = props.location.longitude
  const lat = props.location.latitude
  const [[west, south], [east, north]] = d3.geoBounds(cf)
  if (lat < south - COORD_COUNTRY_PAD || lat > north + COORD_COUNTRY_PAD) return false
  // Longitude: countries spanning the antimeridian report west > east.
  return west <= east
    ? lon >= west - COORD_COUNTRY_PAD && lon <= east + COORD_COUNTRY_PAD
    : lon >= west - COORD_COUNTRY_PAD || lon <= east + COORD_COUNTRY_PAD
})

// Whether there's anything to draw at all.
const hasMap = computed(() => coordTrusted.value || !!countryFeature.value)

function draw() {
  const el = mapEl.value
  if (!el) return
  d3.select(el).selectAll('svg').remove()

  let projection
  let markerPoint = null
  const highlightId = coordTrusted.value ? null : countryFeature.value?.id

  if (coordTrusted.value) {
    const lon = props.location.longitude
    const lat = props.location.latitude
    projection = d3.geoMercator().center([lon, lat]).scale(SCALE).translate([W / 2, H / 2])
    markerPoint = [lon, lat]
  } else if (countryFeature.value) {
    // Centre on the country at the same scale as the marker case, so the zoom
    // level stays consistent whether we drop a pin or just fill the country.
    const centroid = d3.geoCentroid(countryFeature.value)
    projection = d3.geoMercator().center(centroid).scale(SCALE).translate([W / 2, H / 2])
  } else {
    return
  }

  const path = d3.geoPath().projection(projection)

  const svg = d3.select(el).append('svg')
    .attr('width', '100%')
    .attr('height', '100%')
    .attr('viewBox', `0 0 ${W} ${H}`)
    .attr('preserveAspectRatio', 'xMidYMid slice')

  // Graticule first so even an ocean-only view still reads as a map.
  svg.append('path')
    .datum(d3.geoGraticule().step([15, 15])())
    .attr('d', path)
    .attr('fill', 'none')
    .attr('stroke', GRATICULE)
    .attr('stroke-width', 0.5)

  svg.append('g')
    .selectAll('path')
    .data(getFeatures())
    .join('path')
    .attr('d', path)
    .attr('fill', d => (highlightId != null && d.id === highlightId) ? HIGHLIGHT_FILL : LAND_FILL)
    .attr('stroke', LAND_STROKE)
    .attr('stroke-width', 0.5)
    .attr('stroke-linejoin', 'round')

  if (markerPoint) {
    const point = projection(markerPoint)
    if (!point) return
    const [x, y] = point
    svg.append('circle')
      .attr('cx', x).attr('cy', y).attr('r', 11)
      .attr('fill', ACCENT)
      .attr('opacity', 0.28)
      .attr('class', 'ipgeo-halo')
    svg.append('circle')
      .attr('cx', x).attr('cy', y).attr('r', 4.5)
      .attr('fill', ACCENT_TEXT)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5)
  }
}

onMounted(draw)
watch(() => props.location, draw, { deep: true })
onBeforeUnmount(() => {
  if (mapEl.value) d3.select(mapEl.value).selectAll('svg').remove()
})
</script>

<template>
  <div class="ip-geo-card">
    <div v-if="hasMap" ref="mapEl" class="geo-map"></div>
    <div v-else class="geo-map geo-map-empty">
      <font-awesome-icon :icon="['fas', 'location-dot']" class="geo-empty-icon" />
      <span>No location for this IP</span>
    </div>

    <!-- Floating dark notch: flag + City, Country over the map -->
    <div class="geo-notch">
      <img
        v-if="flagSrc"
        class="geo-flag"
        height="18"
        :src="flagSrc"
        :alt="location?.country_iso"
      >
      <div class="geo-caption">
        <span class="geo-place">{{ caption || 'Unknown location' }}</span>
        <span v-if="secondary" class="geo-secondary">{{ secondary }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ip-geo-card {
  position: relative;
  /* The card background IS the frame — the map is inset 3px so this shows through. */
  background: var(--surface-card);
  border: none;
  border-radius: var(--radius-xl);
  overflow: hidden;
  /* Height auto + grid's default stretch → the card fills its row, matching
     the Infrastructure/Signals cards beside it. min-height stops it collapsing
     when it sits on a row by itself (its map/notch are absolutely positioned). */
  min-height: 260px;
}

/* Map fills the card, inset 3px so the dark card frames it. */
.geo-map {
  position: absolute;
  inset: 3px;
  border-radius: calc(var(--radius-xl) - 3px);
  overflow: hidden;
  /* Darker "water" so land tiles stand out against it. */
  background: #0e1319;
}

.geo-map-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--text-faint);
  font-size: 0.85rem;
}

.geo-empty-icon {
  font-size: 1.5rem;
  opacity: 0.6;
}

/* Dark notch anchored to the bottom-left frame — same colour, no border,
   so it reads as part of the card rather than a floating chip. */
.geo-notch {
  position: absolute;
  left: 3px;
  bottom: 3px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  max-width: calc(100% - 6px);
  padding: 0.5rem 0.8rem;
  background: var(--surface-card);
  border: none;
  border-top-right-radius: var(--radius-lg);
  border-bottom-left-radius: calc(var(--radius-xl) - 3px);
}

.geo-flag {
  border-radius: var(--radius-xs);
  flex-shrink: 0;
}

.geo-caption {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.geo-place {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-heading);
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.geo-secondary {
  font-size: 0.7rem;
  color: var(--text-faint);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
</style>

<!-- Unscoped: animate the d3-created halo (scoped styles can't reach it). -->
<style>
@keyframes ipgeo-pulse {
  0% { transform: scale(1); opacity: 0.28; }
  70% { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.2); opacity: 0; }
}
.ipgeo-halo {
  transform-box: fill-box;
  transform-origin: center;
  animation: ipgeo-pulse 2.4s ease-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .ipgeo-halo { animation: none; }
}
</style>
